import { useState, useEffect } from 'react';
import { FALLBACK_SVG_DATA_URI } from '../utils/imageFallback';

/**
 * SafeImage - Image component with intelligent fallback handling
 * Prevents infinite browser retries and console errors for missing images
 * 
 * Features:
 * - Preloads images before rendering
 * - Validates fallback images
 * - Uses data URI as final fallback (no network request)
 * - Prevents browser retry loops
 */
export function SafeImage({ 
  src, 
  fallbackSrc = null, 
  alt = '', 
  className = '',
  loading = 'lazy',
  ...restProps 
}) {
  const [imageSrc, setImageSrc] = useState(FALLBACK_SVG_DATA_URI);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!src) {
      setImageSrc(FALLBACK_SVG_DATA_URI);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    // Preload primary image
    const primaryImg = new Image();
    
    primaryImg.onload = () => {
      if (isMounted) {
        setImageSrc(src);
        setIsLoading(false);
      }
    };

    primaryImg.onerror = () => {
      // Primary failed, try fallback if provided
      if (fallbackSrc && fallbackSrc !== src && isMounted) {
        const fallbackImg = new Image();
        
        fallbackImg.onload = () => {
          if (isMounted) {
            setImageSrc(fallbackSrc);
            setIsLoading(false);
          }
        };

        fallbackImg.onerror = () => {
          // Fallback also failed, use data URI
          if (isMounted) {
            setImageSrc(FALLBACK_SVG_DATA_URI);
            setIsLoading(false);
          }
        };

        fallbackImg.src = fallbackSrc;
      } else {
        // No fallback, use data URI immediately
        if (isMounted) {
          setImageSrc(FALLBACK_SVG_DATA_URI);
          setIsLoading(false);
        }
      }
    };

    primaryImg.src = src;

    return () => {
      isMounted = false;
      primaryImg.onload = null;
      primaryImg.onerror = null;
    };
  }, [src, fallbackSrc]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      loading={loading}
      onError={(e) => {
        // Final safety net - should rarely trigger due to preloading
        e.currentTarget.onerror = null;
        e.currentTarget.src = FALLBACK_SVG_DATA_URI;
      }}
      {...restProps}
    />
  );
}

export default SafeImage;
